/**
 * LaTeX diagnostics (error/warning markers) for Monaco Editor.
 *
 * Uses the AST-based parser from latex-parser.ts to detect:
 *  • Missing \end{…} for an opened \begin{…}
 *  • Unmatched \end{…} without a preceding \begin{…}
 *  • Unmatched braces { }
 *  • Syntax errors reported by latex-utensils
 *
 * Usage:
 *   Call `attachDiagnostics(editor, monaco)` after the editor mounts.
 *   Returns a disposable to tear down.
 */

import type { editor, IDisposable } from "monaco-editor"
import { parseLatex } from "./latex-parser"
import { LATEX_LANGUAGE_ID } from "./monaco-latex"

const DIAGNOSTICS_OWNER = "latex-diagnostics"

/**
 * Attach live diagnostics to a Monaco editor instance.
 * Re-parses on every content change (debounced) and sets markers.
 */
export function attachDiagnostics(
  editorInstance: editor.IStandaloneCodeEditor,
  monaco: typeof import("monaco-editor"),
): IDisposable {
  const updateMarkers = () => {
    const model = editorInstance.getModel()
    if (!model) return
    if (model.getLanguageId() !== LATEX_LANGUAGE_ID) {
      monaco.editor.setModelMarkers(model, DIAGNOSTICS_OWNER, [])
      return
    }

    const source = model.getValue()
    const result = parseLatex(source)

    const markers: editor.IMarkerData[] = result.diagnostics.map((d) => ({
      severity:
        d.severity === "error"
          ? monaco.MarkerSeverity.Error
          : monaco.MarkerSeverity.Warning,
      startLineNumber: d.startLine,
      startColumn: d.startCol,
      endLineNumber: d.endLine,
      endColumn: d.endCol,
      message: d.message,
      source: "LaTeX",
      owner: DIAGNOSTICS_OWNER,
    }))

    monaco.editor.setModelMarkers(model, DIAGNOSTICS_OWNER, markers)
  }

  // Debounced update (500ms to avoid thrashing while typing)
  let timer: ReturnType<typeof setTimeout> | null = null
  const debouncedUpdate = () => {
    if (timer) clearTimeout(timer)
    timer = setTimeout(updateMarkers, 500)
  }

  const disposables: IDisposable[] = []

  disposables.push(editorInstance.onDidChangeModelContent(debouncedUpdate))

  // Run once immediately
  updateMarkers()

  return {
    dispose() {
      for (const d of disposables) d.dispose()
      if (timer) clearTimeout(timer)
      // Clear markers
      const model = editorInstance.getModel()
      if (model) {
        monaco.editor.setModelMarkers(model, DIAGNOSTICS_OWNER, [])
      }
    },
  }
}
